import { type ReactNode, useMemo } from 'react';
import {
  FeatureManagementProvider as FeatureManagementProviderComponent,
} from '@printdeal/feature-management/react';
import { useBreakpoint } from '@printdeal/ui-library';
import { useIntl } from 'react-intl';
import { useSegmentation } from '@printdeal/segmentation/react';
import { windowPresent } from '../../helpers/window';
import { getStore } from '../../helpers/env';
import { getConfiguration } from '../../helpers/Configuration';
import { isRecognizedUser } from '../../templates/homepage/homepage-helper';
import { useAccount } from '../../hooks/useAccount';
import { useCustomer } from '../../hooks/useCustomer';

const getTier = () => {
  if (!windowPresent) {
    return 'development';
  }

  if (window.navigator.userAgent.includes('Playwright')) {
    return 'test';
  }

  return window.location.hostname === getConfiguration().channel.url ? 'production' : 'development';
};

interface Props {
  children: ReactNode;
}

const FeatureManagementProvider = ({ children }: Props) => {
  const { locale } = useIntl();
  const { account } = useAccount();
  const { customer } = useCustomer();
  const isDesktop = useBreakpoint('lg');
  const segmentationData = useSegmentation();

  const user = useMemo(() => {
    const customerSegmentAttributes = Object.create(null);

    if (segmentationData.hasData) {
      const traits = segmentationData.getTraits();
      if (traits.customerSegmentLevel0) {
        customerSegmentAttributes.customerSegmentLevel0 = traits.customerSegmentLevel0;
      }
      if (traits.customerSegmentLevel1) {
        customerSegmentAttributes.customerSegmentLevel1 = traits.customerSegmentLevel1;
      }
      if (traits.customerSegmentLevel2) {
        customerSegmentAttributes.customerSegmentLevel2 = traits.customerSegmentLevel2;
      }
    }

    return {
      email: account?.email || undefined,
      store: getStore(),
      locale,
      custom: {
        isRecognized: isRecognizedUser(account?.id || ''),
        device: isDesktop ? 'desktop' : 'mobile',
        isLoggedIn: Boolean(account?.email),
        isCompany: Boolean(customer?.custom?.fields?.isCompany),
        audiences: segmentationData.hasData ? segmentationData.getAudiences() : undefined,
        isReseller: segmentationData.hasData ?? segmentationData.isReseller(),
        isEnduser: segmentationData.hasData ?? segmentationData.isEndUser(),
        ...customerSegmentAttributes,
      },
    };
  }, [locale, account?.email, customer?.custom?.fields?.isCompany, isDesktop, segmentationData, account?.id]);

  if (!process.env.GATSBY_STATSIG_CLIENT_KEY) {
    throw new Error('Environment variable "GATSBY_STATSIG_CLIENT_KEY" is not set.');
  }

  return (
    <FeatureManagementProviderComponent
      user={user}
      apiKey={process.env.GATSBY_STATSIG_CLIENT_KEY}
      environment={getTier()}
    >
      {children}
    </FeatureManagementProviderComponent>
  );
};

export default FeatureManagementProvider;
